import AgyleDialog from '../../../../components/modal/agyleDialog';
import React, {useEffect, useState} from 'react';
import Typography from '@mui/material/Typography';
import {useTranslation} from 'react-i18next';
import _ from 'lodash';
import moment from 'moment/moment';
import Notes from '../../../../util/notes';
import API from '../../../../app/api';
import {setSnackbar} from '../../../../store/reducers/globals';
import {useDispatch, useSelector} from 'react-redux';
import {user} from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import InformationBox from '../../../../components/other/informationBox';
import {getConfig} from '../../../../util/configUtil';
import {ctxMandatoryAssessmentList, setValueToModify} from '../../../../store/reducers/patientContext';

const DEFAULT_STATE = {
    title: 'Warning',
    body: null,
    submitButtonText: 'Submit',
    disableSubmit: false,
};

const MainScreenWarningDialog = ({patient, open, onClose, type, onAction}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // User object
    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);

    // Selectors
    const assessmentList = useSelector(ctxMandatoryAssessmentList);

    const [context, setContext] = useState(DEFAULT_STATE);

    // Configs
    const timestampBlockedDischarge = getConfig('timestampBlockedDischarge', false);

    useEffect(() => {
        if (open) {
            switch (type) {
                case 'demographic':
                    setContext(prevState => ({
                        ...prevState,
                        body: (
                            <Typography
                                variant="h6"
                                gutterBottom
                                align="center"
                            >
                                {t('Are you sure you want to confirm the patient\'s demographics?')}
                            </Typography>
                        ),
                    }));
                    break;
                case 'seeAndTreatDraft':
                    setContext(prevState => ({
                        ...prevState,
                        body: (
                            <InformationBox type="warning">
                                {t('The draft See and Treat form must be completed to discharge this patient.')}
                            </InformationBox>
                        ),
                        disableSubmit: true,
                    }));
                    break;
                case 'timestamp':
                    setContext(prevState => ({
                        ...prevState,
                        body: (
                            <>
                                <InformationBox type="warning">
                                    {t('Some forms are still waiting to be completed after the timestamp has been set.')}
                                </InformationBox>
                                {!timestampBlockedDischarge &&
                                    <p style={{textAlign: 'center'}}>
                                        {t('Do you wish to proceed to the Discharge Form screen?')}
                                    </p>
                                }
                                {timestampBlockedDischarge &&
                                    <p style={{textAlign: 'center'}}>
                                        {t('Please complete the following forms before discharge:')}
                                    </p>
                                }
                                {ptHaveTimestamp()}
                            </>
                        ),
                        submitButtonText: t('Confirm'),
                        disableSubmit: timestampBlockedDischarge,
                    }));
                    break;
                case 'safeguarding':
                    setContext(prevState => ({
                        ...prevState,
                        body: (
                            <InformationBox type="warning">
                                {t('ECDS Safeguarding is mandatory for discharge.')}
                            </InformationBox>
                        ),
                        disableSubmit: true,
                    }));
                    break;
                case 'sepsis':
                    setContext(prevState => ({
                        ...prevState,
                        body: (
                            <InformationBox type="warning">
                                {t('All Sepsis Screening Tools and Sepsis Pathways must be completed/canceled before discharge of the patient.')}
                            </InformationBox>
                        ),
                        disableSubmit: true,
                    }));
                    break;
                case 'vod':
                    setContext(prevState => ({
                        ...prevState,
                        body: (
                            <InformationBox type="warning">
                                {t('A Verification of Death form must be completed before the patient is discharged.')}
                            </InformationBox>
                        ),
                        disableSubmit: true,
                    }));
                    break;
                case 'mandatoryAssessment':
                    setContext(prevState => ({
                        ...prevState,
                        body: (
                            <>
                                <InformationBox type="warning">
                                    {t('Mandatory assessments must be completed before discharge.')}
                                </InformationBox>
                                {showMandatoryAssessment()}
                            </>
                        ),
                        disableSubmit: true,
                    }));
                    break;
                default:
                    console.error('Wrong type', type);
                    setContext(DEFAULT_STATE);
            }
        }
    }, [open, type, assessmentList]);

    function ptHaveTimestamp() {
        const clerking = patient?.clerking?.status === 'timestamp';
        const triage = patient?.triage && !patient?.triage?.discriminator && patient?.triage?.triageClinicians;
        const rapid = patient?.seen?.status === 'timestamp';
        const seeAndTreat = patient?.studies?.seeAndTreat?.status === 'timestamp';

        return (
            <>
                {!timestampBlockedDischarge && t('The following forms have a timestamp:')}
                <ul>
                    {clerking && <li>Clerking</li>}
                    {triage && <li>Triage</li>}
                    {rapid && <li>Rapid Assessment</li>}
                    {seeAndTreat && <li>See And Treat</li>}
                </ul>
            </>
        );
    }

    function showMandatoryAssessment() {
        return (
            <ul>
                {assessmentList.map((assessment, index) => (
                    <li key={index}>{assessment}</li>
                ))}
            </ul>
        );
    }

    const handleAction = () => {
        switch (type) {
            case 'demographic':
                handleConfirmDemographics();
                break;
            case 'timestamp':
            case 'seeAndTreatDraft':
                handleClose();
                if (type === 'demographic' && !timestampBlockedDischarge) alert('Open Discharge somehow');
                break;
            default:
                console.error('Wrong type', type);
        }
    };

    const handleClose = () => {
        onClose();
        setContext(DEFAULT_STATE);
    };

    const handleConfirmDemographics = () => {
        if (patient?.patient) {
            let tags = _.cloneDeep(patient?.patient?.tags) || [];
            tags = _.filter(tags, t => t !== 'fastRegistry');
            const date = moment().toISOString();
            const data = [{
                name: 'metadata.ptUpdate',
                value: {
                    by: userFullName,
                    login: userObject?.userName,
                    when: date,
                },
                noteToAdd: Notes.getConfirmDemographicsNote(userFullName, date),
            }];

            API.updatePatientTags(patient?.patient?.__id, tags)
                .then(res => {
                    dispatch(setValueToModify(data));
                })
                .catch(e => {
                    console.error(e);
                    dispatch(setSnackbar({type: 'error', message: 'Patient update failed.'}));
                })
                .finally(() => handleClose());
        }
    };
    return (
        <AgyleDialog
            title={t(context.title)}
            open={open}
            onSave={handleAction}
            onClose={handleClose}
            width="md"
            warning
            submitText={context.submitButtonText}
            disableSave={context.disableSubmit}
        >
            <>
                {context.body}
            </>
        </AgyleDialog>
    );
};

export default MainScreenWarningDialog;